import type { EPatientGender } from 'src/types/patient'
import type { EMedAlertUi } from 'src/types/med-alert'
import type { TSmsLog } from 'src/types/sms-log'
import type { TLabel } from 'src/types/locales'
import type { EVariableType } from 'src/types/variable'

export type TPatientConversationQuestion = {
  variableId: number
  variableLabel: string
  variableType: EVariableType
  patientAnswer: string | null
}

export type TPatientConversationDetail = {
  id: number
  formLabel: string | null
  firstSmsLogSentAt: string | null
  questions: TPatientConversationQuestion[]
}

export enum EHumanReviewResolvedBy {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export enum EHumanReviewRequestSubject {
  UNKNOWN = 'UNKNOWN',
  ADMINISTRATIVE_DOCUMENTS = 'ADMINISTRATIVE_DOCUMENTS',
  MEDICAL_DOCUMENTS = 'MEDICAL_DOCUMENTS',
  CONVOCATION = 'CONVOCATION',
  POST_OP_CONTROL = 'POST_OP_CONTROL',
}

export enum EHumanReviewTab {
  ALL = 'ALL',
  ADMINISTRATIVE_DOCUMENTS = 'ADMINISTRATIVE_DOCUMENTS',
  MEDICAL_DOCUMENTS = 'MEDICAL_DOCUMENTS',
  CONVOCATION = 'CONVOCATION',
  POST_OP_CONTROL = 'POST_OP_CONTROL',
}

export enum EHumanReviewRequestProblem {
  SMS_NOT_RECEIVED = 'SMS_NOT_RECEIVED',
  MISSING_DOCUMENT = 'MISSING_DOCUMENT',
  CONVOCATION_DATE_CHANGED = 'CONVOCATION_DATE_CHANGED',
  NO_RESPONSE = 'NO_RESPONSE',
  INCOMPLETE_QUESTIONNAIRE = 'INCOMPLETE_QUESTIONNAIRE',
  DECLARED_COMPLICATION = 'DECLARED_COMPLICATION',
  APPOINTMENT_CANCELLED = 'APPOINTMENT_CANCELLED',
  APPOINTMENT_RESCHEDULE_REQUESTED = 'APPOINTMENT_RESCHEDULE_REQUESTED',
  MISSING_DISCHARGE_COMPANION = 'MISSING_DISCHARGE_COMPANION',
  UNKNOWN = 'UNKNOWN',
}

export const isHumanReviewRequestProblem = (value: unknown): value is EHumanReviewRequestProblem =>
  Object.values(EHumanReviewRequestProblem).includes(value as EHumanReviewRequestProblem)

export enum EHumanReviewResolution {
  PROCESSED = 'PROCESSED',
  UNREACHABLE = 'UNREACHABLE',
  CONTACTED = 'CONTACTED',
}

export enum EHumanReviewChangeType {
  CREATED = 'CREATED',
  RESOLVED = 'RESOLVED',
  PATIENT_ARCHIVED = 'PATIENT_ARCHIVED',
  PATIENT_UNARCHIVED = 'PATIENT_UNARCHIVED',
  PATIENT_DELETED = 'PATIENT_DELETED',
  PATIENT_MED_EVENT_CHANGED = 'PATIENT_MED_EVENT_CHANGED',
}

export const SUBJECTS_WITH_EXTENDED_RESOLUTIONS: EHumanReviewRequestSubject[] = [
  EHumanReviewRequestSubject.CONVOCATION,
  EHumanReviewRequestSubject.POST_OP_CONTROL,
]

export const getEffectiveResolution = (
  subject: EHumanReviewRequestSubject,
  resolution: EHumanReviewResolution,
): EHumanReviewResolution =>
  SUBJECTS_WITH_EXTENDED_RESOLUTIONS.includes(subject) ? resolution : EHumanReviewResolution.PROCESSED

export type THumanReviewTab = {
  subject: EHumanReviewTab
  toProcessCount: number
  isVisible: boolean
  problemCounts: Partial<Record<EHumanReviewRequestProblem, number>>
  resolutionCounts: Partial<Record<EHumanReviewResolution, number>>
}

export type THumanReviewTabsResponse = {
  tabs: THumanReviewTab[]
}

export type THumanReviewListPayload = {
  subject?: EHumanReviewTab
  problemsFilters?: EHumanReviewRequestProblem[]
  resolutionsFilters?: EHumanReviewResolution[]
  medEventIdsFilters?: number[]
  limit?: number
  cursor?: string
}

export type THumanReviewListResponse = {
  items: THumanReviewListItem[]
  // null on the last page
  nextCursor: string | null
}

export type THumanReviewListItem = {
  patientMedEventId: number
  patientMedEventDate: string
  resolution: EHumanReviewResolution | null
  patient: {
    firstName: string | null
    lastName: string | null
  }
}

export type THumanReviewRequest = {
  id: number
  subject: EHumanReviewRequestSubject
  problem: EHumanReviewRequestProblem
  resolution: EHumanReviewResolution | null
  resolvedBy: EHumanReviewResolvedBy | null
  resolvedByUserId: number | null
  resolvedAt: string | null
  resolutionComment: string | null
  createdAt: string
  PatientMedEventId: number
  PatientConversationId: number | null
  PatientConversation: TPatientConversationDetail | null
  Variable: { id: number; intlLabel: TLabel } | null
  SmsLogId: number | null
  SmsLog: TSmsLog | null
  PreviousOutboundSmsLog: TSmsLog | null
  PatientMedAlertId: number | null
  PatientMedAlert: { MedAlert: { ui: EMedAlertUi; label: string } } | null
}

export const matchesActiveSubject = (
  requestSubject: EHumanReviewRequestSubject,
  activeSubject: EHumanReviewTab,
): boolean => {
  if (activeSubject === EHumanReviewTab.ALL) return true
  return (requestSubject as string) === (activeSubject as string)
}

export const subjectToTab = (subject: EHumanReviewRequestSubject): EHumanReviewTab | null => {
  const mapping: Record<string, EHumanReviewTab> = {
    [EHumanReviewRequestSubject.ADMINISTRATIVE_DOCUMENTS]: EHumanReviewTab.ADMINISTRATIVE_DOCUMENTS,
    [EHumanReviewRequestSubject.MEDICAL_DOCUMENTS]: EHumanReviewTab.MEDICAL_DOCUMENTS,
    [EHumanReviewRequestSubject.CONVOCATION]: EHumanReviewTab.CONVOCATION,
    [EHumanReviewRequestSubject.POST_OP_CONTROL]: EHumanReviewTab.POST_OP_CONTROL,
  }
  return mapping[subject] ?? null
}

export type THumanReviewDetail = {
  patientMedEvent: {
    id: number
    date: string
    // Current convocation date of the appointment, i.e. the new one when the date has changed
    convocationDate: string | null
    PatientFollowupId: number
    MedEventId: number
  }
  patient: {
    id: number
    remoteId: string | null
    firstName: string | null
    lastName: string | null
    gender: EPatientGender
    phone: string | null
    hasCaregiver: boolean
    isPhoneValid: boolean
  }
  reviewRequests: THumanReviewRequest[]
}
